<template>
  <div class="not-login-chat">
    <div class="not-login-chat-header">
      <slot name="header"></slot>
    </div>
    <div
      class="not-login-chat-content"
      v-if="pageStatus === PAGE_STATUS.LOGIN_IN"
    >
      <SignIn
        :page-type="1"
        :default-email="submitForm.email"
        :action-type="signActionType"
        :show-header="true"
        @update:action="updateAction"
      />
    </div>
    <div
      class="not-login-chat-content"
      v-else-if="pageStatus === PAGE_STATUS.NOT_LOGIN"
    >
      <div class="not-login-chat-content-inner">
        <slot name="content"></slot>
        <InitChatContent @sign-in="goToSignIn" />
      </div>
      <div class="not-login-chat-footer">
        <div
          class="not-login-chat-footer-img"
          v-if="submitForm.imgFiles.length"
        >
          <div
            class="not-login-chat-img-item"
            v-for="item in submitForm.imgFiles"
            :key="item.name"
            :class="{ 'vac-blur-loading': item.loading }"
            :style="{
              'background-image': `url('${item.localUrl || item.url}')`,
            }"
          >
            <van-icon
              name="close"
              size="22"
              class="not-login-chat-img-item-close"
              @click="removeImage(item)"
            />
          </div>
        </div>
        <van-form>
          <van-field
            v-model="submitForm.email"
            class="not-login-chat-footer-email"
            name="email"
            label=""
            :placeholder="emailFocus ? '' : 'email@example.com'"
            :rules="[
              {
                pattern: emailPattern,
                message: t('message.notLogin.emailTip'),
              },
            ]"
            @focus="focusEmail"
            @blur="blurEmail"
          />
          <van-field
            v-model="submitForm.message"
            :rows="1"
            label=""
            type="textarea"
            :autosize="{ maxHeight: 50 }"
            :placeholder="t('message.notLogin.question')"
          />
        </van-form>
        <div class="not-login-chat-footer-button" v-click-outside="closeEmoji">
          <van-uploader :after-read="afterRead">
            <img src="@/assets/svg-icons/img.svg" />
          </van-uploader>
          <emoji-picker-container
            class="vac-message-emojis"
            emoji-reaction
            :emoji-opened="emojiOpened"
            :pc-mode="true"
            @add-emoji="sendMessageReaction"
            @open-emoji="openEmoji"
          ></emoji-picker-container>

          <van-button
            round
            :class="{ 'van-button--disabled': buttonDisabled }"
            :loading="userStore.autoRegisterLoading || emailCheckLoading"
            class="send-button"
            type="primary"
            @click="sendMessage"
          >
            <img src="@/assets/svg-icons/upArrow.svg" />
          </van-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// import { Button } from 'vant'
import { ref, computed, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { ElMessage } from "element-plus";
import { useUserStore } from "@/states/user";
import { useMsgStore } from "@/states/msg";
import { emailCheck } from "@/api/user";
import { getFileDimensions } from "@/utils/tool";
import { RegisterType } from "@/components/sign-in/constant";
import SignIn from "@/components/sign-in/Index.vue";
import EmojiPickerContainer from "./EmojiPickerContainer/EmojiPickerContainer.vue";
import InitChatContent from "./InitChatContent.vue";
import vClickOutside from "./on-click-outside";
import { Action, ActionType } from "@/components/sign-in/constant";

const PAGE_STATUS = {
  NOT_LOGIN: 0,
  LOGIN_IN: 1,
};
const { t } = useI18n();
const userStore = useUserStore();
const msgStore = useMsgStore();

// 邮箱格式 或者为空
const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$|^$/;
const emojiOpened = ref(false);
const emailCheckLoading = ref(false);
const pageStatus = ref(PAGE_STATUS.NOT_LOGIN);
const signActionType = ref(ActionType.Login);
const submitForm = ref({
  email: "",
  message: "",
  imgFiles: [] as any[],
});
const buttonDisabled = computed(() => {
  return (
    (!submitForm.value.message && !submitForm.value.imgFiles.length) ||
    !submitForm.value.email ||
    !emailCheckResult.value
  );
});
const emailCheckResult = computed(() => {
  return submitForm.value.email && emailPattern.test(submitForm.value.email);
});
const emailFocus = ref(false);

const sendMessageReaction = (e) => {
  submitForm.value.message += e.unicode;
};

const openEmoji = () => {
  emojiOpened.value = true;
};
const closeEmoji = () => {
  emojiOpened.value = false;
};

const afterRead = async (sourceFile) => {
  const file = sourceFile.file;
  const { width, height } = await getFileDimensions(file);
  const fileURL = URL.createObjectURL(file);
  const typeIndex = file.name.lastIndexOf(".");
  const loadedFile = {
    name: file.name.substring(0, typeIndex),
    size: file.size,
    type: file.type,
    extension: file.name.substring(typeIndex + 1),
    localUrl: fileURL,
    width,
    height,
    blob: null as any,
  };
  const blobFile = await fetch(fileURL).then((res) => res.blob());
  loadedFile.blob = blobFile;
  submitForm.value.imgFiles.push(loadedFile);
};

const sendMessage = () => {
  if (buttonDisabled.value) {
    ElMessage.warning({
      message: t("message.signIn.submitTips"),
    });
    return;
  }
  if (!emailCheckResult.value) {
    return;
  }
  emailCheckLoading.value = true;
  emailCheck({
    userName: submitForm.value.email,
  })
    .then((res) => {
      if (res.data) {
        // 已注册，则登录
        goToSignIn(ActionType.RegisterAutoLogin);
      } else {
        // 未注册，则自动注册
        userStore
          .autoRegister(
            submitForm.value.email,
            RegisterType.AUTO_REGISTER_EMAIL,
          )
          .then(() => {
            console.log("autoRegister success");
          })
          .catch(() => {
            // 自动注册失败，则登录
            goToSignIn(ActionType.RegisterAutoLogin);
          });
      }
    })
    .finally(() => {
      emailCheckLoading.value = false;
    });
  msgStore.catchMessage = {
    content: submitForm.value.message,
    replyMessage: "",
    files: submitForm.value.imgFiles,
  };
  userStore.shouldCheckMessage = true;
};

const removeImage = (item) => {
  // 释放对象URL以避免内存泄漏
  if (item.localUrl) {
    URL.revokeObjectURL(item.localUrl);
  }
  submitForm.value.imgFiles = submitForm.value.imgFiles.filter(
    (file) => file.localUrl !== item.localUrl,
  );
};

const goToSignIn = (actionType: ActionType) => {
  pageStatus.value = PAGE_STATUS.LOGIN_IN;
  signActionType.value = actionType;
};

const updateAction = (action) => {
  if (action === Action.Back) {
    pageStatus.value = PAGE_STATUS.NOT_LOGIN;
  }
};

const focusEmail = () => {
  emailFocus.value = true;
};

const blurEmail = () => {
  emailFocus.value = false;
};

// 组件卸载时清理所有对象URL
onUnmounted(() => {
  submitForm.value.imgFiles.forEach((file) => {
    if (file.localUrl) {
      URL.revokeObjectURL(file.localUrl);
    }
  });
});
</script>

<style scoped lang="scss">
.not-login-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}
.not-login-chat-header {
  width: 100%;
  display: flex;
  background-color: #fff;
  height: 50px;
  border-bottom: 1px solid #e1e4e8;
}
.not-login-chat-content {
  flex: 1;
  flex-direction: column;
  display: flex;
  background-color: #fff;
}
.not-login-chat-content-inner {
  flex: 1;
}
.not-login-chat-footer {
  background-color: #fff;
  padding: 13px 0px;
  box-shadow: 0px 2px 10px 0px rgba(217, 217, 217, 0.5);
  border-radius: 10px;
  border: 1px solid #fff1ca;
  margin: 0 10px 10px;
}
.not-login-chat-footer-button {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 15px;
}
.not-login-chat-footer-img {
  display: flex;
  overflow: auto;
  width: 100%;
  padding: 0 12px;
  margin-bottom: 10px;
  .not-login-chat-img-item {
    width: 100px;
    height: 100px;
    margin-right: 10px;
    border-radius: 4px;
    border: 1px solid #e1e4e8;
    position: relative;
    flex-shrink: 0;
  }
  .not-login-chat-img-item-close {
    position: absolute;
    left: 6px;
    top: 6px;
    z-index: 10;
  }
}
.vac-message-emojis {
  margin-left: 14px;
  height: 20px;
}
</style>
<style lang="scss">
.not-login-chat-footer {
  .van-cell {
    &::after {
      display: none;
    }
  }
  .van-uploader {
    height: 20px;
  }
}
.not-login-chat-footer-email {
  padding-bottom: 0px;
  padding-top: 0px;
  .van-field__body {
    width: 100%;
    position: relative;
    padding-bottom: 10px;
    &::after {
      position: absolute;
      box-sizing: border-box;
      content: " ";
      pointer-events: none;
      right: 0;
      bottom: 0;
      left: 0;
      border-bottom: 1px solid #eaeaea;
    }
  }
  .van-field__error-message {
    border-top: 1px solid #ee0a24;
  }
}
.not-login-chat-footer-button {
  .van-button--primary {
    &::before {
      min-width: 28px;
      min-height: 28px;
    }
  }
}
.send-button {
  width: 30px;
  height: 30px;
  padding: 0;
  margin-left: 14px;
  .van-button__text {
    padding-top: 2px;
  }
  &.van-button--disabled {
    background: #e0e0e0;
    border: 1px solid #e0e0e0;
  }
}
</style>
